/* eslint-disable no-case-declarations */
import Vue from 'vue'
import { updateField } from 'vuex-map-fields'

import { initialState } from '@/store/state'

export const mutations = {
  updateField,

  authSuccess (state, token) {
    state.token = token
  },

  authError (state) {
    state.token = ''
  },

  /** Reset state */
  reset (state) {
    // Note: not clearing sessionStorage here, as we need it to restore window
    // after log out due to inactivity, and sessionStorage doesn't persist after tab closed.
    // If we need to clear, let's clear specific keys from it, so we don't lose everything.
    // sessionStorage.clear()
    Object.assign(state, initialState())
  },

  setRefreshTokenExpiresAt (state, payload) {
    state.refreshTokenExpiresAt = payload
  },

  setRefreshTokenExpiresAtWarnLimit (state, payload) {
    state.refreshTokenExpiresAtWarnLimit = payload
  },

  /** USERS */
  setCurrentUser (state, userData) {
    const user = userData
    user.isAdmin = userData.user_type === 'admin'
    user.atleastEditor = userData.user_type === 'editor' || user.isAdmin
    user.atleastContributor = (userData.user_type === 'contributor' || userData.user_type === 'external') || user.atleastEditor
    user.atleastCommenter = userData.user_type === 'commenter' || user.atleastContributor
    user.atleastViewer = userData.user_type === 'viewer' || user.atleastCommenter
    // A user should not be able to create a record if they won't be able to access it:
    user.canCreateRecord =
      user.atleastEditor && (user.all_teams || (userData.teams && userData.teams.length > 0))

    user.isExternal = userData.user_type === 'external'
    state.currentUser = {
      ...state.currentUser,
      ...user,
    }
  },
  addUserModel (state, value) {
    state.models.users.push(value.user)
  },
  setUserModel (state, user) {
    const index = state.models.users.findIndex(u => u.id === user.id)

    if (index >= 0) Vue.set(state.models.users, index, user)
  },
  removeUserModel (state, value) {
    if (value.index !== -1) state.models.users.splice(value.index, 1)
  },
  setUsersModel (state, value) {
    state.models.users = value
  },
  setUserTypeOptionsModel (state, value) {
    state.models.userTypeOptions = value
  },

  /** CASES */
  setDocketEntrySearchResultsModel (state, value) {
    state.models.caseEventSearchResults = value
  },

  /** PARTIES */
  // Note: this model is used throughout the editing process in PartyEdit.vue,
  // so should likely remain used exclusively for that. It is distinct from
  // state.models.editedParty
  setCurrentRecordPartiesModel (state, value) {
    if (Array.isArray(value)) state.models.currentRecordParties = value
    else {
      // Merge response with our stored state
      const partyData = {
        ...state.models.currentRecordParties[value.partyIndex],
        ...value.partyData,
      }
      Vue.set(state.models.currentRecordParties, value.partyIndex, partyData)
    }
  },

  updateExistingRecordPartyModel (state, party) {
    const idx = state.models.currentRecordParties.findIndex(p => p.id === party.id)
    if (idx < 0) return
    Vue.set(state.models.currentRecordParties, idx, party)
  },

  /** DOCKET ENTRIES */
  setAllDocketEntriesModel (state, value) {
    state.models.currentUserAllDocketEntries = value
  },
  setDocketEntriesModel (state, value) {
    state.models.currentDocketEntries = value
  },
  updateDocketEntriesModel (state, value) {
    if (value.index !== -1) Vue.set(state.models.currentDocketEntries, value.index, value.data)
  },
  rehydrateDocketEntryModel (state, { docketEntryId, data }) {
    const index = state.models.currentDocketEntries.findIndex(d => d.id === docketEntryId)
    if (index < 0) return

    Vue.set(state.models.currentDocketEntries, index, {
      ...state.models.currentDocketEntries[index],
      ...data,
    })
  },
  deleteDocketEntryModel (state, value) {
    if (value !== -1) Vue.delete(state.models.currentDocketEntries, value)
  },
  deleteDocketEntryRelation (state, value) {
    const parent = state.models.currentDocketEntries.find(d => d.id === value.parent_docket_entry_id)
    const child = state.models.currentDocketEntries.find(d => d.id === value.child_docket_entry_id)

    let relationIndex = (parent?.child_docket_entry_relations || []).findIndex(r => r.id === value.id)
    if (relationIndex >= 0) parent.child_docket_entry_relations.splice(relationIndex, 1)

    relationIndex = (child?.parent_docket_entry_relations || []).findIndex(r => r.id === value.id)
    if (relationIndex >= 0) child.parent_docket_entry_relations.splice(relationIndex, 1)
  },
  setDocketEntriesInAllCasesModel (state, value) {
    state.search.docketEntrySearchResults = value
  },

  /** Dashboard */
  setDashboardDates (state, value) {
    state.dashboard[`${value.type.toLowerCase()}Dates`] = value.data
  },
  setDashboardUsers (state, value) {
    state.dashboard[`${value.type.toLowerCase()}Users`] = value.data
  },

  /** Dialogs */
  setViewAdverseIndividualsDialog (state, value) {
    state.dialogOpenStates.viewAdverseIndividuals = value
  },
  setCreateCaseDialog (state, value) {
    state.dialogOpenStates.createCase = value
  },
  setEditCaseDialog (state, value) {
    state.dialogOpenStates.editCase = value
  },
  setCreateRecordDialog (state, value) {
    state.dialogOpenStates.createRecord = value
  },
  setEditRecordDialog (state, value) {
    state.dialogOpenStates.editRecord = value
  },
  setEditUserDialog (state, value) {
    state.dialogOpenStates.editUser = value
  },
  setDataTypeDialog (state, value) {
    Vue.set(state.dialogOpenStates.dataTypes, value.type, value.openStatus)
  },
  openEntriesMassAssignmentDialog (state, value) {
    state.dialogOpenStates.openEntriesMassAssignmentDialog = value
  },

  /** SETTINGS */
  setGendersModel (state, value) {
    state.models.genders = value
  },
  setCountriesModel (state, value) {
    state.models.countries = value
  },

  /** SETTINGS, Editable */
  setSiteSettings (state, value) {
    if (value.fromSettings) state.settings.retrieved.siteSettings = true
    else state.settings.retrieved.siteSettings = false

    if (Array.isArray(value.data)) {
      value.data.forEach((setting, index) => {
      // need to turn JSON string into an object
        if (setting.name === 'invoiced' && value.fromSettings) setting.value = JSON.parse(setting.value)

        Vue.set(state.siteSettings, index, setting)
      })
    } else {
      // need to turn JSON string into an object
      if (value.data.name === 'invoiced') value.data.value = JSON.parse(value.data.value)

      const lastIndex = state.siteSettings.length
      Vue.set(state.siteSettings, lastIndex, value.data)
    }
  },
  updateSiteSetting (state, value) {
    if (value.index !== -1) Vue.set(state.siteSettings, value.index, value.data)
  },
  deleteSiteSetting (state, value) {
    Vue.delete(state.siteSettings, value.index)
  },
  setMetadata (state, value) {
    state.settings.metadata[value.type] = value.data
  },
  setSettingsDataModel (state, value) {
    state.settings.retrieved[value.type] = true
    if (Array.isArray(value.data)) state.models[value.type] = value.data
    else {
      const lastIndex = state.models[value.type].length
      Vue.set(state.models[value.type], lastIndex, value.data)
    }
  },
  updateSettingsDataModel (state, value) {
    if (value.index !== -1) Vue.set(state.models[value.type], value.index, value.data)
  },
  deleteSettingsDataModel (state, value) {
    if (value.index !== -1) Vue.delete(state.models[value.type], value.index)
  },

  /** Settings, for data fetched outside of Settings views */
  setPreferenceCategoriesModel (state, value) {
    if (Array.isArray(value)) state.models.preferenceCategories = value
    else {
      const lastIndex = state.models.preferenceCategories.length
      Vue.set(state.models.preferenceCategories, lastIndex, value)
    }
  },
  setDocketEntryTypesModel (state, value) {
    if (Array.isArray(value)) state.models.docketEntryTypes = value
    else {
      const lastIndex = state.models.docketEntryTypes.length
      Vue.set(state.models.docketEntryTypes, lastIndex, value)
    }
  },
  setFilteredDocketEntryTypesModel (state, value) {
    if (Array.isArray(value)) state.models.filteredDocketEntryTypes = value
    else {
      const lastIndex = state.models.filteredDocketEntryTypes.length
      Vue.set(state.models.filteredDocketEntryTypes, lastIndex, value)
    }
  },
  setLegalCaseTypesModel (state, value) {
    if (Array.isArray(value)) state.models.legalCaseTypes = value
    else {
      const lastIndex = state.models.legalCaseTypes.length
      Vue.set(state.models.legalCaseTypes, lastIndex, value)
    }
  },

  setLanguagesModel (state, value) {
    if (Array.isArray(value)) state.models.languages = value
    else {
      const lastIndex = state.models.languages.length
      Vue.set(state.models.languages, lastIndex, value)
    }
  },
  setLocationsModel (state, value) {
    if (Array.isArray(value)) state.models.locations = value
    else {
      const lastIndex = state.models.locations.length
      Vue.set(state.models.locations, lastIndex, value)
    }
  },
  setTeamsModel (state, value) {
    if (Array.isArray(value)) state.models.teams = value
    else {
      const lastIndex = state.models.teams.length
      Vue.set(state.models.teams, lastIndex, value)
    }
  },
  setPartyRolesModel (state, value) {
    if (Array.isArray(value)) state.models.partyRoles = value
    else {
      const lastIndex = state.models.partyRoles.length
      Vue.set(state.models.partyRoles, lastIndex, value)
    }
  },
  setDocumentSetsModel (state, value) {
    if (Array.isArray(value)) state.models.documentSets = value
    else {
      const lastIndex = state.models.documentSets.length
      Vue.set(state.models.documentSets, lastIndex, value)
    }
  },
  updateDocumentSetsModel (state, value) {
    if (value.index !== -1) Vue.set(state.models.documentSets, value.index, value.data)
  },

  /** FILES */
  updateFileNameInDocketEntry (state, value) {
    if (value.index !== -1 && value.fileIndex !== -1) {
      value.forDocketEntry
        ? Vue.set(state.models.currentDocketEntries[value.index].files, value.fileIndex, value.data)
        : Vue.set(state.models.documentSets[value.index].files, value.fileIndex, value.data)
    }
  },
  deleteAttachmentModel (state, value) {
    if (value.index !== -1 && value.fileIndex !== -1) {
      value.forDocketEntry
        ? Vue.delete(state.models.currentDocketEntries[value.index].files, value.fileIndex)
        : Vue.delete(state.models.documentSets[value.index].files, value.fileIndex)
    }
  },

  /** WORKFLOWS */
  setLegalCaseTypesWorkflowModel (state, value) {
    if (Array.isArray(value.data)) state.models.legalCaseTypes[value.index].workflows = value.data
    else {
      const lastIndex = state.models.legalCaseTypes[value.index].workflows.length
      Vue.set(state.models.legalCaseTypes[value.index].workflows, lastIndex, value.data)
    }
  },
  setDocketEntryTypesWorkflowModel (state, value) {
    if (Array.isArray(value.data)) state.models.docketEntryTypes[value.index].workflows = value.data
    else {
      const lastIndex = state.models.docketEntryTypes[value.index].workflows.length
      Vue.set(state.models.docketEntryTypes[value.index].workflows, lastIndex, value.data)
    }
  },
  updateLegalCaseTypesWorkflowModel (state, value) {
    if (value.workflowIndex === -1) {
      const lastIndex = state.models.legalCaseTypes[value.index].workflows.length
      Vue.set(state.models.legalCaseTypes[value.index].workflows, lastIndex, value.data)
    } else {
      Vue.set(state.models.legalCaseTypes[value.index].workflows,
        value.workflowIndex, value.data)
    }
  },
  updateDocketEntryTypesWorkflowModel (state, value) {
    if (value.workflowIndex === -1) {
      const lastIndex = state.models.docketEntryTypes[value.index].workflows.length
      Vue.set(state.models.docketEntryTypes[value.index].workflows, lastIndex, value.data)
    } else {
      Vue.set(state.models.docketEntryTypes[value.index].workflows,
        value.workflowIndex, value.data)
    }
  },
  deleteLegalCaseTypesWorkflowModel (state, value) {
    Vue.delete(state.models.legalCaseTypes[value.index].workflows, value.workflowIndex)
    if (state.models.legalCaseTypes[value.index].workflows.length === 0) state.models.legalCaseTypes[value.index].has_workflows = false
  },
  deleteDocketEntryTypesWorkflowModel (state, value) {
    Vue.delete(state.models.docketEntryTypes[value.index].workflows, value.workflowIndex)
    if (state.models.docketEntryTypes[value.index].workflows.length === 0) state.models.docketEntryTypes[value.index].has_workflows = false
  },

  /** ALERTS
   * we only keep track of a single alert at a time,
   * no need to add 'update' or 'delete' methods for this reason
  */
  setAlert (state, value) {
    state.alert = value
  },

  /** Record search term
   * This sets the search term for the Record view,
   * when the user has done a search, clicks on a result but
   * is able to reload the search if going back to the Record view
   */
  setRecordViewSearch (state, value) {
    state.recordSearchData = value
  },

  /** REPORTS */
  setReportData (state, value) {
    state.reportsData[value.type] = value.data
  },
  setReportType (state, value) {
    state.reportsData.currentType = value
  },

  /** AUTOMATED TEXT TEMPLATE */
  setDocketEntryTypesAutomatedTextTemplateModel (state, value) {
    state.models.docketEntryTypes[value.index].automated_text_template = value.data.automated_text_template
  },

  /**
   * TODO: Move to SettingsModule
   */
  updateModelDependentsFormFields: (state, value) => {
    const updatedModelList = state.models[value.type]

    if (value.type === 'checklists') {
      // DocketEntryTypesUpdate
      const docketEntryTypeChecklist = state.settings.metadata?.docketEntryTypes?.fields.find(f => f.id === 'checklist_id')
      const mappedModelField = updatedModelList ? updatedModelList.map(m => ({ text: m.title, value: m.id })) : []

      docketEntryTypeChecklist.items = mappedModelField
    }
  },
}
